import { API_URL } from '$lib/globals.js';
import type { LoadEvent } from '@sveltejs/kit';
import ky, {
  HTTPError,
  type KyInstance,
  type Options as KyOptions,
  type ResponsePromise,
} from 'ky';
import type { paths } from './interfaces.generated.js';
import type {
  OpHeaderType,
  OpJsonBodyType,
  OpReturnType,
  OpSearchParamsType,
} from './types.js';

export type HttpMethod = 'get' | 'post' | 'put' | 'patch' | 'head' | 'delete';

const apiUrlWithTrailingSlash = API_URL.endsWith('/') ? API_URL : `${API_URL}/`;

/** The global `ky` instance to use to communicate with the API.
 *
 * **WARNING:** This instance is not available on the server-side.
 *              In `load` functions, use the `apiClientForLoadEvent()` helper instead.
 */
export const apiClient = ky.create({
  prefixUrl: apiUrlWithTrailingSlash,
  credentials: 'include',
});

/**
 * From a load event that provides a `fetch` function and a page `url`,
 * build an API client that will work server-side.
 */
export function apiClientForLoadEvent(
  loadEvent: Pick<LoadEvent, 'fetch' | 'url'>,
) {
  return ky.create({
    fetch: loadEvent.fetch,
    // Build full URL to the API endpoint using the `url` provided by the load event.
    prefixUrl: apiUrlWithTrailingSlash.startsWith('/')
      ? new URL(apiUrlWithTrailingSlash, loadEvent.url.href).href
      : apiUrlWithTrailingSlash,
    credentials: 'include',
  });
}

/**
 * Wraps an API call to match paths and operations defined in the OpenAPI spec.
 *
 * @param path The path of the API endpoint to call.
 * @param client A Ky instance to use to make the request.
 *               Optional, defaults to the global `apiClient`.
 *
 *
 * @example
 * await api('/hello')
 *   .call({ method: 'post', json: { foo: bar } })
 *   .json();
 */
export function api<Path extends `/${string}` & keyof paths>(
  path: Path,
  client?: KyInstance,
) {
  const internalApiClient = client ?? apiClient;
  return {
    call: <Method extends HttpMethod & keyof paths[Path]>(
      options: { method: Method } & Omit<KyOptions, 'method'> &
        OpJsonBodyType<paths[Path][Method]> &
        OpSearchParamsType<paths[Path][Method]> &
        // Headers are still optional because they might be defined in the global config
        Partial<OpHeaderType<paths[Path][Method]>>,
    ) =>
      internalApiClient(
        // Remove the leading slash from the path
        path.slice(1),
        { ...options },
      ) as Omit<ResponsePromise, 'json'> & {
        json: () => Promise<OpReturnType<paths[Path][Method]>>;
      },
  };
}

// Re-export some classes from `ky` for convenience.
export { HTTPError };
